import React from "react";
import { Button as AntButton } from "antd";
import type { ButtonProps as AntButtonProps } from "antd/lib/button";
import { cn } from "../../utils/cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "secondary2"
  | "danger"
  | "reject"
  | "ghost"
  | "link"
  | "text"
  | "default"
  | "dashed"
  | "success"
  | "approve"
  | "warning";

export type ButtonSize = "small" | "middle" | "large";

export interface ButtonProps extends Omit<AntButtonProps, "type" | "size"> {
  appearance?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
}

const ANTD_TYPE_MAP: Record<ButtonVariant, AntButtonProps["type"]> = {
  primary: "primary",
  secondary: "default",
  secondary2: "default",
  danger: "primary",
  reject: "default",
  ghost: "default",
  link: "link",
  text: "text",
  dashed: "dashed",
  default: "default",
  success: "primary",
  approve: "primary",
  warning: "primary",
};

const BASE = "!inline-flex !items-center !justify-center gap-2 transition-all duration-150 font-bold cursor-pointer";

const SIZE_CLASSES: Record<ButtonSize, string> = {
  small: "!h-[36px] !px-3.5 !rounded-[9px] !text-[12px]",
  middle: "!h-[40px] !px-[18px] !rounded-[11px] !text-[13px]",
  large: "!h-[46px] !px-5 !rounded-[11px] !text-[14px]",
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: cn(
    BASE,
    "!bg-[linear-gradient(135deg,#6D28D9,rgba(109,40,217,0.8))] !border-0 !text-white",
    "!shadow-[0_8px_18px_-8px_rgba(109,40,217,0.6)]",
    "hover:!shadow-[0_10px_22px_-8px_rgba(109,40,217,0.7)] hover:!-translate-y-0.5 active:!translate-y-0 active:!scale-[0.98]",
    "focus-visible:!ring-2 focus-visible:!ring-primary-500 focus-visible:!ring-offset-2",
  ),
  secondary: cn(
    BASE,
    "!bg-white hover:!bg-slate-50",
    "!border !border-slate-200 hover:!border-slate-300",
    "!text-slate-700 hover:!text-slate-900",
    "focus-visible:!ring-2 focus-visible:!ring-primary-500 focus-visible:!ring-offset-2",
  ),
  secondary2: cn(
    BASE,
    "!bg-white hover:!bg-[var(--color-bg-muted)]",
    "!border !border-[var(--color-border-primary)]",
    "!text-[var(--color-text-secondary)]",
    "focus-visible:!ring-2 focus-visible:!ring-primary-500 focus-visible:!ring-offset-2",
  ),
  danger: cn(
    BASE,
    "!bg-red-600 hover:!bg-red-700 active:!bg-red-800",
    "!border-red-600 hover:!border-red-700",
    "!text-white",
    "focus-visible:!ring-2 focus-visible:!ring-red-500 focus-visible:!ring-offset-2",
  ),
  reject: cn(
    BASE,
    "!bg-white hover:!bg-red-50",
    "!border !border-red-200 hover:!border-red-300",
    "!text-red-600 hover:!text-red-700",
    "active:!scale-[0.98]",
    "focus-visible:!ring-2 focus-visible:!ring-red-500 focus-visible:!ring-offset-2",
  ),
  approve: cn(
    BASE,
    "!bg-gradient-to-r !from-emerald-500 !to-emerald-600 !border-0 !text-white",
    "!shadow-[0_6px_16px_-6px_rgba(16,185,129,0.5)]",
    "hover:!shadow-[0_8px_20px_-6px_rgba(16,185,129,0.6)] hover:!-translate-y-0.5 active:!translate-y-0 active:!scale-[0.98]",
    "focus-visible:!ring-2 focus-visible:!ring-emerald-500 focus-visible:!ring-offset-2",
  ),
  success: cn(
    BASE,
    "!bg-gradient-to-r !from-emerald-500 !to-emerald-600 !border-0 !text-white",
    "!shadow-[0_6px_16px_-6px_rgba(16,185,129,0.5)]",
    "hover:!shadow-[0_8px_20px_-6px_rgba(16,185,129,0.6)] hover:!-translate-y-0.5 active:!translate-y-0 active:!scale-[0.98]",
    "focus-visible:!ring-2 focus-visible:!ring-emerald-500 focus-visible:!ring-offset-2",
  ),
  warning: cn(
    BASE,
    "!bg-amber-500 hover:!bg-amber-600 active:!bg-amber-700",
    "!border-amber-500 hover:!border-amber-600",
    "!text-white",
    "focus-visible:!ring-2 focus-visible:!ring-amber-500 focus-visible:!ring-offset-2",
  ),
  ghost: cn(
    BASE,
    "hover:!bg-primary-50 active:!bg-primary-100",
    "!text-primary-600 hover:!text-primary-700",
    "focus-visible:!ring-2 focus-visible:!ring-primary-500 focus-visible:!ring-offset-2",
  ),
  link: cn(BASE, "!text-primary-600 hover:!text-primary-700 !h-auto !p-0 !shadow-none"),
  text: cn(BASE, "hover:!bg-slate-100 !border-0 !shadow-none"),
  dashed: cn(BASE, "!border-dashed !border-slate-300 hover:!border-primary-400 hover:!text-primary-600"),
  default: cn(
    BASE,
    "!bg-white hover:!bg-slate-50",
    "!border !border-slate-200 hover:!border-slate-300",
    "!text-slate-700",
  ),
};

const Button: React.FC<ButtonProps> = ({
  appearance = "primary",
  size = "middle",
  className,
  children,
  ...rest
}) => {
  return (
    <AntButton
      type={ANTD_TYPE_MAP[appearance]}
      size={size}
      className={cn(
        SIZE_CLASSES[size],
        VARIANT_CLASSES[appearance],
        (rest.disabled || rest.loading) && "!opacity-50 !cursor-not-allowed !pointer-events-none",
        className,
      )}
      {...rest}
    >
      {children}
    </AntButton>
  );
};

export default Button;

